/**
 * Outdoor themes (+field), picked by `DungeonDef.field.theme`: 'town' (Mossvale), 'meadow'
 * (Sunmeadow), 'forest' (Bramblewood). Pure data: ground palette, edge dressing, interior scatter,
 * grass, sky and lighting. outdoor/layout.ts lays them out, outdoor/field.ts builds the meshes.
 *
 * Brighter than the dungeons (sky + hemisphere light, soft fog), but the walkable ground stays a
 * mid value so telegraphs and effects still read on it (art direction: floor vs effects contrast).
 */
import type { DungeonDef, RoomDef } from '@keystone/sim';
import type { DungeonTheme, RGB, RoomStyle, ThemeLighting } from '../themes/types';

/** Weighted proc / kit ids with a scale range. */
export interface ScatterItem {
  id: string;
  w: number;
  scale: readonly [number, number];
  /** Footprint radius at scale 1 (yards): spacing between props. */
  r: number;
  /** Height at scale 1 (yards). */
  h: number;
  /** Small walk-through decor: hidden on the low preset, never casts shadows. */
  detail?: boolean;
  /** Variant count (`id#0..n-1`); default 3. */
  variants?: number;
  /** Per-instance multiply tint range. */
  tint?: readonly [RGB, RGB];
  /** +valley: sink into the ground (yd at scale 1; big rocks / trees on slopes). */
  sink?: number;
}

export type EdgeLine = 'fence' | 'hedge' | 'bush' | 'rocks' | 'bramble' | 'mossrock' | 'fern';

export interface OutdoorTheme {
  id: 'town' | 'meadow' | 'forest';
  name: string;
  ground: {
    /** Grass: base, light patches, dark patches (sRGB hex). */
    grass: readonly [number, number, number];
    dirt: number;
    /** Darkening under the edge trees. */
    shade: number;
    /** Town plaza cobbles (multiplied over the cobble texture). */
    cobble?: number;
    /** +valley: cool darkening toward the walkable edge (value framing: bright centre, dark rim). */
    edgeDark?: number;
    /** +valley: large soft sunlit patches (yellow-green), 0..1. */
    sunPatches?: number;
    /** +zone: exposed stone and scree patches on the ground, 0..1. */
    rock?: number;
    /** +zone: warmer, lighter ground (meadow light) vs cooler, darker (forest floor): -1..1. */
    warmth?: number;
  };
  /** Height (yd) the terrain rises to beyond the edge band (hills / valley walls). */
  hills: number;
  /** Worn dirt paths between the entrance, portals and the map centre (yd wide; 0 = none). */
  pathWidth: number;
  /** Line dressing just outside the walkable edge. */
  edgeLine: readonly EdgeLine[];
  /** Near edge band (dense, full trees). */
  edgeBand: number;
  edgeSpacing: number;
  edgeTrees: readonly ScatterItem[];
  /** Under-storey around the edge trees (bushes, rocks, brambles). */
  edgeUnder: readonly ScatterItem[];
  /** Far ring tint (instanced distant trees); +detail: `roundLeaf` colours the round broadleaf far trees. */
  farTree: { leaf: number; kind: 'round' | 'pine' | 'mixed' | 'spruce'; roundLeaf?: number };
  /** Obstacle fill (trees / rocks standing on DungeonDef.obstacles). */
  obstacleTrees: readonly ScatterItem[];
  obstacleRocks: readonly ScatterItem[];
  /** Walk-through decor on the walkable ground (flowers, pebbles, mushrooms). */
  decor: readonly ScatterItem[];
  /** Decor items per 100 yd² of walkable ground. */
  decorDensity: number;
  /** +detail: walk-through nature props over the open ground (bushes, boulders, logs) and their density per 100 yd². */
  scatter?: readonly ScatterItem[];
  scatterDensity?: number;
  /** +detail: road verge dressing. */
  verge?: readonly ScatterItem[];
  /** `fromGround`: tufts take the terrain colour under them, `colors` multiply it (base, tip). */
  grass: { perYd2: number; colors: readonly [number, number]; height: number; fromGround?: boolean };
  /** `clouds`: painted cumulus (bigger, softer, lit from the sun side) instead of the default wisps. */
  sky: { top: number; mid: number; horizon: number; cloud: number; cloudShade: number; clouds?: { scale: number; cover: number; soft: number } };
  lighting: ThemeLighting & { fogNear: number; fogFar: number; fogAmt: number; keyIntensity: number };
  // ---- +valley (Sunmeadow art pass, outdoor/valley.ts); all optional, other themes unchanged ----
  /** Proc renames: a placed proc `name` becomes one of these (picked per placement). */
  remap?: Readonly<Record<string, readonly string[]>>;
  /** Big rock formations in the band [e0, e1] yd outside the walkable edge (valley walls). */
  cliffs?: { items: readonly ScatterItem[]; band: readonly [number, number]; spacing: number; density: number };
  /** Flagstones along the paths (else worn dirt only). */
  flagstones?: boolean;
  /** The dungeon portal as a ruin arch on a rock outcrop with a crystal monument (else the turf mound). */
  barrowRuin?: boolean;
  /** Half-angle (rad) of the notch thinned in the edge dressing toward the Tower (seen from the entrance and the hub). */
  vistaNotch?: number;
  /** Distant mountain layers (outdoor/backdrop.ts): far / mid / near silhouette colours and the sunlit ridge tint. */
  mountains?: { far: number; mid: number; near: number; light: number };
  /** The Tower on the horizon as a pale ruined spire. */
  towerStyle?: 'ruin';
  /** Crystal glow colour (linear RGB). */
  crystalGlow?: RGB;
  /**
   * +zone: per-biome looks on a zone map (FieldAreaDef.biome): ground palette, dressing sets and
   * densities override the theme's own where the area's biome asks for them.
   */
  biomes?: Readonly<Record<string, BiomeLook>>;
}

/** +zone: what a biome changes (unset = the theme's). */
export interface BiomeLook {
  ground?: Partial<OutdoorTheme['ground']>;
  edgeTrees?: readonly ScatterItem[];
  edgeUnder?: readonly ScatterItem[];
  obstacleTrees?: readonly ScatterItem[];
  obstacleRocks?: readonly ScatterItem[];
  /** Chance a small obstacle gets rocks instead of a tree. */
  rockChance?: number;
  decor?: readonly ScatterItem[];
  decorDensity?: number;
  /** Grass density multiplier. */
  grassK?: number;
  /** Edge cliff density multiplier. */
  cliffK?: number;
  /** Far-ring tree tint (multiplied over the far leaf colour). */
  farTint?: RGB;
  /** Hill height beyond the edge and how fast it rises (yd; small = crags). */
  hills?: number;
  hillSharp?: number;
  /** +detail: open-ground scatter, its density, verge dressing, the far ring's share of round trees. */
  scatter?: readonly ScatterItem[];
  scatterDensity?: number;
  verge?: readonly ScatterItem[];
  farRound?: number;
}

const s = (id: string, w: number, scale: readonly [number, number], r: number, h: number, extra: Partial<ScatterItem> = {}): ScatterItem => ({ id, w, scale, r, h, ...extra });
const leafVar: readonly [RGB, RGB] = [
  [0.86, 0.94, 0.84],
  [1.08, 1.06, 0.96],
];
const stoneVar: readonly [RGB, RGB] = [
  [0.86, 0.86, 0.84],
  [1.04, 1.02, 0.98],
];

// ---- +detail: tree mixes, open-ground scatter and verges per biome ----------------------------------
const d = (id: string, w: number, scale: readonly [number, number], r: number, h: number, extra: Partial<ScatterItem> = {}): ScatterItem => s(id, w, scale, r, h, { detail: true, ...extra });
/** Broadleaf trees (outdoor/props/trees.ts) at the size their obstacles expect. */
const OAK = (w: number, sc: readonly [number, number] = [0.75, 1.05]): ScatterItem => s('tree_oak_round', w, sc, 2.2, 9, { sink: 0.2 });
const OAK_WIDE = (w: number): ScatterItem => s('tree_oak_wide', w, [0.75, 1.0], 2.6, 11.5, { sink: 0.2 });
const BIRCH = (w: number): ScatterItem => s('tree_birch', w, [0.8, 1.15], 1.3, 9, { sink: 0.15 });
const LINDEN = (w: number): ScatterItem => s('tree_linden', w, [0.75, 1.05], 2.0, 11, { sink: 0.2 });
const POPLAR = (w: number): ScatterItem => s('tree_poplar', w, [0.8, 1.1], 1.2, 12, { sink: 0.2 });
const WILLOW = (w: number): ScatterItem => s('tree_willow', w, [0.8, 1.1], 2.4, 8.5, { sink: 0.2 });
const APPLE = (w: number): ScatterItem => s('tree_apple', w, [0.85, 1.15], 1.8, 5.6, { sink: 0.1 });
const BLOSSOM = (w: number): ScatterItem => s('tree_blossom_round', w, [0.8, 1.05], 1.8, 7, { sink: 0.15 });
const DARK_OAK = (w: number): ScatterItem => s('tree_oak_dark', w, [0.8, 1.1], 2.2, 10, { sink: 0.2 });
const SPRUCE = (w: number, sc: readonly [number, number] = [0.65, 1.0]): ScatterItem => s('spruce', w, sc, 1.8, 12.5);
const FIR = (w: number, sc: readonly [number, number] = [0.75, 1.1]): ScatterItem => s('fir', w, sc, 2.2, 8.5);
const YOUNG = (w: number): ScatterItem => s('spruce_young', w, [0.9, 1.25], 1.3, 4.6);
/** Shrubs and bushes round the copses (rim ring) and in the under-storey. */
const UNDER_MEADOW: readonly ScatterItem[] = [s('bush_round', 4, [0.9, 1.4], 0.9, 1.3), s('bush_flowering', 2, [0.9, 1.3], 0.9, 1.2), s('shrub', 2, [0.9, 1.3], 0.9, 1.3), s('mossrock', 2, [0.7, 1.2], 1.0, 0.9, { sink: 0.15 }), d('fern', 3, [0.9, 1.4], 0.8, 0.7), d('bush_grass', 3, [0.9, 1.3], 0.5, 1), d('flowers_daisy', 1, [1, 1.3], 0.8, 0.4)];
/** Open meadow scatter: bushes in groups, boulders of every size, now and then a log or a stump. */
const SCATTER_MEADOW: readonly ScatterItem[] = [
  s('bush_round', 6, [0.8, 1.5], 0.9, 1.3),
  s('bush_flowering', 3, [0.8, 1.3], 0.9, 1.2),
  s('bush_dense', 2, [0.7, 1.0], 1.1, 1.8),
  s('shrub', 2, [0.8, 1.3], 0.9, 1.3),
  s('boulder_slab', 2, [0.5, 1.4], 1.3, 0.8),
  s('mossrock', 3, [0.5, 1.3], 1.0, 0.9, { sink: 0.15 }),
  s('mossrock_big', 1, [0.6, 1.0], 1.6, 1.4, { sink: 0.2 }),
  s('log_mossy', 1, [0.8, 1.1], 1.8, 0.8),
  s('stump_mossy', 1, [0.8, 1.2], 0.6, 0.8),
  d('bush_grass', 4, [0.9, 1.4], 0.5, 1),
];
const VERGE_MEADOW: readonly ScatterItem[] = [
  d('bush_grass', 6, [0.8, 1.3], 0.5, 1),
  d('flowers_daisy', 3, [0.8, 1.2], 0.8, 0.4),
  d('flowers_butter', 2, [0.8, 1.2], 0.8, 0.4),
  d('flowers_poppy', 1, [0.8, 1.2], 0.8, 0.5),
  d('rock_pebbles', 1, [0.8, 1.3], 0.7, 0.2),
  d('bluebells', 2, [0.9, 1.2], 0.8, 0.4),
  s('bush_round', 1, [0.7, 1.1], 0.9, 1.3),
  s('mossrock', 1, [0.4, 0.8], 1.0, 0.9, { sink: 0.15 }),
];
/** Meadow ground cover: drifts of wildflowers in many colours, ferns, pebbles, toadstools, moss. */
const DECOR_MEADOW: readonly ScatterItem[] = [
  d('bluebells', 4, [0.9, 1.3], 0.8, 0.4),
  d('violets', 2, [0.9, 1.2], 0.8, 0.4),
  d('flowers_daisy', 4, [0.9, 1.3], 0.8, 0.4),
  d('flowers_butter', 3, [0.9, 1.3], 0.8, 0.4),
  d('flowers_poppy', 2, [0.9, 1.3], 0.8, 0.5),
  d('flowers_lupin', 1, [0.9, 1.2], 0.6, 0.8),
  d('flowers_mixed', 2, [0.9, 1.2], 0.8, 0.5),
  d('fern', 2, [0.7, 1.1], 0.8, 0.7),
  d('bush_grass', 3, [0.8, 1.2], 0.5, 1),
  d('rock_pebbles', 1, [0.8, 1.3], 0.7, 0.2),
  d('mossrock', 1, [0.3, 0.5], 0.4, 0.3),
  d('mushrooms', 1, [0.9, 1.3], 0.4, 0.3),
];

/**
 * Sunmeadow (+valley art pass): a sunlit valley. Dark teal conifers and grey-blue mossy cliffs
 * frame the edges, pale ruins stand on the high ground, a blue crystal monument marks the barrow,
 * a flagstone path winds through yellow-green grass with bluebells and ferns; blue haze and
 * layered mountains in the distance, big soft cumulus overhead. Warm light, cool shade.
 */
export const MEADOW: OutdoorTheme = {
  id: 'meadow',
  name: 'Sunmeadow',
  ground: { grass: [0x5c8e3a, 0x8cab46, 0x377654], dirt: 0x8a8558, shade: 0x214a4c, edgeDark: 0.45, sunPatches: 0.7 },
  hills: 13,
  pathWidth: 3.2,
  edgeLine: ['mossrock', 'fern', 'mossrock'],
  edgeBand: 22,
  edgeSpacing: 4.2,
  edgeTrees: [
    s('spruce', 5, [0.75, 1.35], 1.9, 12.5, { sink: 0.3 }),
    s('fir', 4, [0.8, 1.25], 2.4, 8.5, { sink: 0.3 }),
    s('spruce_young', 2, [0.8, 1.3], 1.3, 4.6, { sink: 0.2 }),
  ],
  edgeUnder: [s('shrub', 3, [0.9, 1.3], 0.9, 1.3), s('mossrock', 2, [0.7, 1.2], 1.0, 0.9, { sink: 0.15 }), s('fern', 3, [0.9, 1.4], 0.8, 0.7, { detail: true }), s('bluebells', 1, [1, 1.3], 0.8, 0.4, { detail: true })],
  farTree: { leaf: 0x2a5655, kind: 'spruce', roundLeaf: 0x3d7a46 },
  obstacleTrees: [s('fir', 3, [0.75, 1.05], 2.2, 8.5), s('spruce', 2, [0.65, 0.9], 1.8, 12.5), s('spruce_young', 1, [0.9, 1.2], 1.3, 4.6)],
  obstacleRocks: [s('mossrock_big', 3, [0.8, 1.1], 1.6, 1.4, { sink: 0.15 })],
  decor: DECOR_MEADOW,
  decorDensity: 2.4,
  scatter: SCATTER_MEADOW,
  scatterDensity: 0.3,
  verge: VERGE_MEADOW,
  grass: { perYd2: 0.66, colors: [0xc4d4b4, 0xe6eec4], height: 0.55, fromGround: true },
  // +scenery: deep zenith, clear mid blue, pale cyan horizon; crisp cumulus with cool bellies.
  sky: { top: 0x1c5fd6, mid: 0x58a4ee, horizon: 0xc8e8f6, cloud: 0xffffff, cloudShade: 0x8ea8d6, clouds: { scale: 1.05, cover: 0.65, soft: 0.07 } },
  lighting: {
    background: 0xbcd8f0,
    fog: [0.5, 0.67, 0.9],
    hemiSky: 0xcfe4ff,
    hemiGround: 0x5a7a58,
    hemiIntensity: 0.78,
    key: 0xfff0d0,
    lift: [0.004, 0.012, 0.03],
    // +scenery: aerial perspective that layers instead of whiting out: it starts early and gently,
    // tops out at 68 % (far tree lines stay blue silhouettes), thinner on the heights, with a low
    // valley mist in the distance and a blue shift before the haze.
    fogNear: 28,
    fogFar: 260,
    fogAmt: 0.0,
    fogShape: [0.68, 0.72, 0.3, 0.3],
    fogTint: [0.8, 0.9, 1.06],
    keyIntensity: 3.1,
    sat: 1.17,
    gain: [1.04, 1.01, 0.97],
  },
  remap: {
    tree_round: ['spruce', 'fir'],
    tree_small: ['fir', 'spruce_young'],
    tree_blossom: ['fir'],
    pine: ['spruce'],
    pine_tall: ['spruce'],
    tree_oak: ['fir'],
    tree_oak_big: ['spruce'],
    bush: ['shrub'],
    rock: ['mossrock'],
    rock_small: ['mossrock'],
    rock_big: ['mossrock_big'],
    boulder: ['mossrock_big'],
    cliff_rock: ['cliff_spire', 'cliff_block', 'cliff_spire'],
    standing_stone: ['column_pale'],
    ruin_pillar: ['column_pale'],
    ruin_wall: ['ruin_wall_pale'],
    crystals: ['glow_crystals'],
    barrow_mound: ['barrow_ruin'],
    portal_ring: ['portal_ring_pale'],
    flowers_red: ['violets'],
    flowers_blue: ['bluebells'],
  },
  cliffs: {
    items: [s('cliff_spire', 3, [1.0, 1.8], 2.8, 5.7, { sink: 0.6 }), s('cliff_shelf', 3, [1.0, 1.6], 4.8, 3.2, { sink: 0.4 }), s('cliff_block', 2, [0.9, 1.5], 2.8, 4.3, { sink: 0.4 })],
    band: [4, 40],
    spacing: 7.5,
    density: 0.55,
  },
  flagstones: true,
  barrowRuin: true,
  vistaNotch: 0.15,
  mountains: { far: 0xc0d6ee, mid: 0xa6c2e2, near: 0x8eafd2, light: 0xfdf6ea },
  towerStyle: 'ruin',
  crystalGlow: [0.2, 0.48, 1.0],
  biomes: {
    // +detail: the quiet travel country: conifers and broadleaf copses half and half.
    meadow: {
      ground: { warmth: 0.5 },
      obstacleTrees: [FIR(3), SPRUCE(2), OAK(3), BIRCH(2), LINDEN(2), OAK_WIDE(1), YOUNG(1)],
      edgeTrees: [s('spruce', 5, [0.75, 1.35], 1.9, 12.5, { sink: 0.3 }), s('fir', 4, [0.8, 1.25], 2.4, 8.5, { sink: 0.3 }), OAK(2, [0.8, 1.15]), LINDEN(1), s('spruce_young', 2, [0.8, 1.3], 1.3, 4.6, { sink: 0.2 })],
      edgeUnder: UNDER_MEADOW,
      farRound: 0.3,
    },
    // +zone v2: the Slime Hollows: lush, bright dells; flowers, mushroom clusters and slime puddles.
    dells: {
      ground: { grass: [0x5aa23e, 0x94c852, 0x3c8a4e], sunPatches: 1.1, warmth: 0.7 },
      decor: [d('flowers_daisy', 3, [0.9, 1.3], 0.8, 0.4), d('flowers_tulip', 2, [0.9, 1.3], 0.8, 0.5), d('flowers_mixed', 2, [0.9, 1.3], 0.8, 0.5), d('bluebells', 2, [0.9, 1.3], 0.8, 0.4), d('flowers_butter', 2, [0.9, 1.3], 0.8, 0.4), d('mushrooms', 1, [0.9, 1.3], 0.4, 0.3), d('mushrooms_big', 1, [0.5, 0.8], 0.5, 0.6), d('slime_puddle', 2, [0.8, 1.4], 0.9, 0.1), d('bush_grass', 2, [0.8, 1.2], 0.5, 1)],
      decorDensity: 3.2,
      grassK: 1.15,
      // +detail: soft broadleaf dells: oaks, birches, a blossom tree or two, few conifers.
      obstacleTrees: [OAK(4), BIRCH(3), BLOSSOM(1), LINDEN(1), FIR(2), OAK_WIDE(1)],
      edgeTrees: [OAK(3, [0.8, 1.15]), BIRCH(2), s('fir', 3, [0.8, 1.25], 2.4, 8.5, { sink: 0.3 }), s('spruce', 2, [0.75, 1.2], 1.9, 12.5, { sink: 0.3 }), BLOSSOM(1)],
      edgeUnder: UNDER_MEADOW,
      scatter: [s('bush_round', 6, [0.8, 1.5], 0.9, 1.3), s('bush_flowering', 4, [0.8, 1.3], 0.9, 1.2), s('mossrock', 2, [0.5, 1.1], 1.0, 0.9, { sink: 0.15 }), s('boulder_slab', 1, [0.5, 1.2], 1.3, 0.8), s('stump_mossy', 1, [0.8, 1.2], 0.6, 0.8), d('mushrooms_big', 1, [0.5, 0.8], 0.5, 0.6), d('bush_grass', 3, [0.9, 1.4], 0.5, 1)],
      scatterDensity: 0.42,
      farRound: 0.55,
    },
    // +zone v2: the Boar Run and Brookside Farm: warm, sun-baked fields of barley and hay.
    farm: {
      ground: { grass: [0x7d9a3e, 0xb4b458, 0x5c8a42], dirt: 0x9a7a52, sunPatches: 1.0, warmth: 1.0 },
      decor: [d('barley', 6, [0.9, 1.3], 0.7, 0.9), d('flowers_poppy', 2, [0.8, 1.1], 0.8, 0.5), d('flowers_butter', 2, [0.8, 1.1], 0.8, 0.4), d('flowers_daisy', 1, [0.8, 1.1], 0.8, 0.4), d('bush_grass', 2, [0.8, 1.2], 0.5, 1)],
      decorDensity: 3.0,
      grassK: 0.8,
      // +detail: orchards, oaks and poplar windbreaks round the farm.
      obstacleTrees: [APPLE(3), OAK(3), POPLAR(2), LINDEN(1), FIR(1)],
      edgeTrees: [POPLAR(3), OAK(3, [0.8, 1.15]), s('fir', 2, [0.8, 1.25], 2.4, 8.5, { sink: 0.3 }), APPLE(1), LINDEN(1)],
      edgeUnder: [s('bush_round', 3, [0.9, 1.3], 0.9, 1.3), s('bush_berry', 2, [0.9, 1.3], 0.8, 1.1), s('hay_bale', 1, [0.9, 1.1], 0.8, 1.2), d('bush_grass', 3, [0.9, 1.3], 0.5, 1), d('flowers_poppy', 1, [1, 1.3], 0.8, 0.5)],
      scatter: [s('bush_round', 3, [0.8, 1.3], 0.9, 1.3), s('bush_berry', 2, [0.8, 1.2], 0.8, 1.1), s('boulder_slab', 1, [0.4, 0.9], 1.3, 0.8), s('stump_mossy', 1, [0.8, 1.1], 0.6, 0.8), d('bush_grass', 4, [0.9, 1.4], 0.5, 1)],
      scatterDensity: 0.18,
      farRound: 0.6,
    },
    town: {
      ground: { grass: [0x5f9040, 0x8aac4c, 0x3f7a52] },
      decor: [d('flowers_daisy', 3, [0.9, 1.2], 0.8, 0.4), d('flowers_tulip', 2, [0.9, 1.2], 0.8, 0.5), d('bluebells', 2, [0.9, 1.2], 0.8, 0.4)],
      decorDensity: 0.8,
      grassK: 0.55,
      cliffK: 0.3,
      obstacleTrees: [OAK(3), APPLE(2), BLOSSOM(1), LINDEN(2)],
      edgeTrees: [OAK(3, [0.8, 1.15]), LINDEN(2), BLOSSOM(1), s('fir', 2, [0.8, 1.25], 2.4, 8.5, { sink: 0.3 })],
      edgeUnder: UNDER_MEADOW,
      scatter: [s('bush_round', 3, [0.8, 1.2], 0.9, 1.3), s('bush_flowering', 3, [0.8, 1.2], 0.9, 1.2)],
      scatterDensity: 0.06,
      farRound: 0.6,
    },
    forest: {
      ground: { grass: [0x4c7c3a, 0x6c9442, 0x2e6650], shade: 0x18383a, edgeDark: 0.55, warmth: -0.5 },
      hills: 16,
      edgeTrees: [s('spruce', 6, [0.9, 1.45], 1.9, 12.5, { sink: 0.3 }), s('fir', 4, [0.9, 1.3], 2.4, 8.5, { sink: 0.3 }), s('spruce_young', 1, [0.9, 1.3], 1.3, 4.6, { sink: 0.2 })],
      edgeUnder: [s('shrub', 3, [0.9, 1.4], 0.9, 1.3), s('fern', 4, [1.0, 1.5], 0.8, 0.7, { detail: true }), s('mushrooms', 2, [1, 1.4], 0.4, 0.3, { detail: true }), s('mossrock', 1, [0.7, 1.1], 1.0, 0.9, { sink: 0.15 })],
      obstacleTrees: [s('spruce', 4, [0.85, 1.25], 1.8, 12.5), s('fir', 3, [0.85, 1.2], 2.2, 8.5), s('spruce_young', 1, [1.0, 1.3], 1.3, 4.6), DARK_OAK(1), BIRCH(1)],
      rockChance: 0.15,
      decor: [d('fern', 5, [0.9, 1.4], 0.8, 0.7), d('mushrooms', 2, [1, 1.4], 0.4, 0.3), d('bluebells', 2, [0.9, 1.2], 0.8, 0.4), d('shrub', 1, [0.7, 1.0], 0.8, 1.0), d('mossrock_patch', 2, [0.8, 1.3], 0.6, 0.2), d('mushrooms_big', 1, [0.7, 1.0], 0.5, 0.6), d('violets', 1, [0.9, 1.2], 0.8, 0.4)],
      decorDensity: 2.4,
      scatter: [s('shrub', 4, [0.8, 1.4], 0.9, 1.3), s('log_mossy', 2, [0.9, 1.2], 1.8, 0.8), s('stump_mossy', 2, [0.8, 1.2], 0.6, 0.8), s('mossrock', 3, [0.6, 1.3], 1.0, 0.9, { sink: 0.15 }), s('bush_dense', 1, [0.7, 1.0], 1.1, 1.8), d('fern', 3, [1.0, 1.5], 0.8, 0.7)],
      scatterDensity: 0.3,
      verge: [d('fern', 4, [0.9, 1.3], 0.8, 0.7), d('bush_grass', 3, [0.8, 1.2], 0.5, 1), d('mushrooms', 2, [0.9, 1.3], 0.4, 0.3), d('rock_pebbles', 2, [0.8, 1.2], 0.7, 0.2), d('mossrock_patch', 2, [0.8, 1.2], 0.6, 0.2)],
      farRound: 0.1,
      grassK: 0.8,
      farTint: [0.78, 0.86, 0.86],
    },
    deepforest: {
      ground: { grass: [0x3e6a36, 0x557c3a, 0x234c42], shade: 0x12302f, edgeDark: 0.65, sunPatches: 0.3, warmth: -0.8 },
      hills: 18,
      edgeTrees: [s('spruce', 7, [1.0, 1.55], 1.9, 12.5, { sink: 0.3 }), s('fir', 3, [1.0, 1.35], 2.4, 8.5, { sink: 0.3 })],
      edgeUnder: [s('bramble', 3, [0.9, 1.3], 1.0, 1.2), s('fern', 4, [1.0, 1.5], 0.8, 0.7, { detail: true }), s('mushrooms', 3, [1, 1.4], 0.4, 0.3, { detail: true })],
      obstacleTrees: [s('spruce', 5, [1.0, 1.4], 1.8, 12.5), s('fir', 2, [1.0, 1.3], 2.2, 8.5)],
      rockChance: 0.1,
      decor: [d('fern', 5, [1.0, 1.5], 0.8, 0.7), d('mushrooms', 3, [1, 1.4], 0.4, 0.3), d('bramble', 1, [0.7, 1.0], 0.8, 1.0), d('violets', 1, [0.9, 1.1], 0.8, 0.4), d('mossrock_patch', 3, [0.8, 1.4], 0.6, 0.2), d('mushrooms_big', 1, [0.8, 1.1], 0.5, 0.6)],
      decorDensity: 2.6,
      scatter: [s('log_mossy', 2, [0.9, 1.3], 1.8, 0.8), s('stump_mossy', 2, [0.9, 1.3], 0.6, 0.8), s('mossrock', 3, [0.7, 1.4], 1.0, 0.9, { sink: 0.15 }), s('bramble', 2, [0.9, 1.3], 1.0, 1.2), d('fern', 3, [1.0, 1.6], 0.8, 0.7)],
      scatterDensity: 0.32,
      verge: [d('fern', 4, [0.9, 1.4], 0.8, 0.7), d('mushrooms', 2, [0.9, 1.3], 0.4, 0.3), d('mossrock_patch', 2, [0.8, 1.2], 0.6, 0.2)],
      grassK: 0.6,
      farTint: [0.66, 0.76, 0.78],
    },
    rocky: {
      ground: { grass: [0x6f8c56, 0x8e9e62, 0x5a7062], shade: 0x2c4248, dirt: 0x8c8870, sunPatches: 0.45, rock: 0.5, warmth: -0.1 },
      hills: 24,
      hillSharp: 9,
      edgeTrees: [s('fir', 3, [0.8, 1.2], 2.4, 8.5, { sink: 0.3 }), s('spruce', 2, [0.7, 1.1], 1.9, 12.5, { sink: 0.3 }), s('spruce_young', 3, [0.8, 1.2], 1.3, 4.6, { sink: 0.2 })],
      edgeUnder: [s('mossrock', 4, [0.8, 1.4], 1.0, 0.9, { sink: 0.15 }), s('mossrock_big', 2, [0.7, 1.1], 1.6, 1.4, { sink: 0.2 }), s('fern', 2, [0.9, 1.3], 0.8, 0.7, { detail: true })],
      obstacleTrees: [s('fir', 2, [0.8, 1.1], 2.2, 8.5), s('spruce_young', 2, [0.9, 1.2], 1.3, 4.6), BIRCH(1)],
      obstacleRocks: [s('mossrock_big', 3, [0.9, 1.3], 1.6, 1.4, { sink: 0.15 }), s('cliff_block', 2, [0.55, 0.75], 2.4, 4.3, { sink: 0.3 })],
      rockChance: 0.85,
      decor: [d('scree', 5, [0.8, 1.3], 0.9, 0.3), d('mossrock', 3, [0.3, 0.7], 0.4, 0.3), d('violets', 1, [0.8, 1.1], 0.8, 0.4), d('fern', 1, [0.7, 1.0], 0.8, 0.7), d('rock_pebbles', 2, [0.9, 1.4], 0.7, 0.2), d('flowers_lupin', 1, [0.8, 1.1], 0.6, 0.8)],
      decorDensity: 2.2,
      scatter: [s('boulder_slab', 4, [0.5, 1.8], 1.3, 0.8), s('mossrock', 3, [0.6, 1.4], 1.0, 0.9, { sink: 0.15 }), s('mossrock_big', 2, [0.6, 1.1], 1.6, 1.4, { sink: 0.2 }), s('shrub', 1, [0.7, 1.1], 0.9, 1.3)],
      scatterDensity: 0.2,
      verge: [d('rock_pebbles', 4, [0.9, 1.4], 0.7, 0.2), d('scree', 3, [0.8, 1.2], 0.9, 0.3), d('bush_grass', 2, [0.8, 1.1], 0.5, 1)],
      grassK: 0.22,
      cliffK: 2.2,
      farTint: [0.9, 0.92, 0.95],
    },
    lake: {
      ground: { grass: [0x5c9640, 0x8cb44c, 0x357658], shade: 0x214a4c, sunPatches: 0.8, warmth: 0.2 },
      decor: [d('bluebells', 4, [0.9, 1.3], 0.8, 0.4), d('violets', 2, [0.9, 1.2], 0.8, 0.4), d('fern', 1, [0.7, 1.0], 0.8, 0.7), d('mossrock', 1, [0.3, 0.5], 0.4, 0.3), d('flowers_daisy', 2, [0.9, 1.2], 0.8, 0.4), d('flowers_lupin', 2, [0.9, 1.2], 0.6, 0.8), d('bush_grass', 2, [0.8, 1.2], 0.5, 1), d('rock_pebbles', 1, [0.8, 1.2], 0.7, 0.2)],
      decorDensity: 2.4,
      grassK: 1.0,
      // +detail: willows and birches on the shore.
      obstacleTrees: [WILLOW(3), BIRCH(3), OAK(2), FIR(2)],
      edgeTrees: [WILLOW(2), BIRCH(2), s('fir', 3, [0.8, 1.25], 2.4, 8.5, { sink: 0.3 }), s('spruce', 2, [0.75, 1.2], 1.9, 12.5, { sink: 0.3 }), OAK(1)],
      edgeUnder: UNDER_MEADOW,
      scatter: SCATTER_MEADOW,
      scatterDensity: 0.16,
      farRound: 0.45,
    },
    ruins: {
      ground: { grass: [0x689440, 0x9cb652, 0x467a56], sunPatches: 0.8, warmth: 0.3, rock: 0.15 },
      hills: 20,
      edgeTrees: [s('fir', 3, [0.8, 1.2], 2.4, 8.5, { sink: 0.3 }), s('spruce', 3, [0.8, 1.3], 1.9, 12.5, { sink: 0.3 }), s('spruce_young', 2, [0.8, 1.2], 1.3, 4.6, { sink: 0.2 })],
      obstacleRocks: [s('mossrock_big', 2, [0.8, 1.1], 1.6, 1.4, { sink: 0.15 }), s('column_pale', 1, [0.9, 1.1], 0.8, 4)],
      rockChance: 0.5,
      decor: [d('violets', 4, [0.9, 1.3], 0.8, 0.4), d('bluebells', 3, [0.9, 1.3], 0.8, 0.4), d('mossrock', 2, [0.3, 0.6], 0.4, 0.3), d('flowers_daisy', 2, [0.8, 1.1], 0.8, 0.4), d('flowers_lupin', 1, [0.8, 1.1], 0.6, 0.8), d('rock_pebbles', 2, [0.8, 1.2], 0.7, 0.2), d('bush_grass', 2, [0.8, 1.2], 0.5, 1)],
      decorDensity: 2.2,
      obstacleTrees: [FIR(3), SPRUCE(2), YOUNG(2), LINDEN(1), BIRCH(1)],
      scatter: [s('boulder_slab', 3, [0.5, 1.5], 1.3, 0.8), s('mossrock', 3, [0.6, 1.3], 1.0, 0.9, { sink: 0.15 }), s('shrub', 2, [0.8, 1.3], 0.9, 1.3), s('bush_round', 2, [0.8, 1.2], 0.9, 1.3), d('bush_grass', 3, [0.9, 1.3], 0.5, 1)],
      scatterDensity: 0.16,
      grassK: 0.85,
      cliffK: 1.2,
    },
  },
};

export const FOREST: OutdoorTheme = {
  id: 'forest',
  name: 'Bramblewood',
  ground: { grass: [0x4f7f36, 0x628f40, 0x3d6430], dirt: 0x7a6146, shade: 0x2f4a2a },
  hills: 5,
  pathWidth: 2.8,
  edgeLine: ['bramble', 'bush', 'rocks'],
  edgeBand: 18,
  edgeSpacing: 3.6,
  edgeTrees: [s('tree_oak', 4, [0.85, 1.15], 1.8, 7.4, { tint: leafVar }), s('pine', 4, [0.85, 1.2], 1.4, 7.5), s('pine_tall', 2, [0.9, 1.1], 1.5, 9.5), s('tree_oak_big', 1, [0.9, 1.05], 2.2, 9)],
  edgeUnder: [s('bramble', 4, [0.9, 1.3], 1.0, 1.2), s('bush_dark', 3, [0.9, 1.3], 1.0, 1.4, { tint: leafVar }), s('log', 1, [0.9, 1.1], 1.2, 0.6), s('stump', 1, [0.9, 1.2], 0.5, 0.6), s('mushrooms', 2, [1, 1.4], 0.4, 0.3, { detail: true }), s('rock', 1, [0.8, 1.2], 0.9, 0.8, { tint: stoneVar })],
  farTree: { leaf: 0x2f5a36, kind: 'mixed' },
  obstacleTrees: [s('tree_oak', 3, [0.9, 1.2], 1.8, 7.4, { tint: leafVar }), s('pine', 3, [0.9, 1.2], 1.4, 7.5), s('pine_tall', 1, [0.9, 1.1], 1.5, 9.5)],
  obstacleRocks: [s('boulder', 3, [0.8, 1.2], 1.4, 1.6, { tint: stoneVar }), s('rock_big', 2, [0.8, 1.1], 1.4, 1.4, { tint: stoneVar })],
  decor: [s('mushrooms', 3, [1, 1.4], 0.4, 0.3, { detail: true }), s('rock_small', 2, [0.6, 1.0], 0.4, 0.3, { detail: true, tint: stoneVar }), s('flowers_blue', 1, [0.8, 1.1], 0.8, 0.4, { detail: true })],
  decorDensity: 0.8,
  grass: { perYd2: 0.5, colors: [0x4a7a34, 0x6f9a44], height: 0.62 },
  sky: { top: 0x3a72c0, mid: 0x8fb4dc, horizon: 0xd8e4d0, cloud: 0xf2f4ee, cloudShade: 0xa8b4c8 },
  lighting: {
    background: 0x9cb8c8,
    fog: [0.42, 0.55, 0.52],
    hemiSky: 0xc8e0e8,
    hemiGround: 0x4a5a36,
    hemiIntensity: 0.66,
    key: 0xffeccc,
    lift: [0.01, 0.016, 0.02],
    fogNear: 55,
    fogFar: 150,
    fogAmt: 0.0,
    keyIntensity: 2.8,
  },
};

export const TOWN: OutdoorTheme = {
  id: 'town',
  name: 'Mossvale',
  ground: { grass: [0x648f40, 0x7aa24c, 0x507838], dirt: 0x8f7354, shade: 0x46633a, cobble: 0xb0a898 },
  hills: 4,
  pathWidth: 3.6,
  edgeLine: ['fence', 'hedge'],
  edgeBand: 12,
  edgeSpacing: 5.5,
  edgeTrees: [s('tree_round', 4, [0.85, 1.15], 1.6, 6.2, { tint: leafVar }), s('tree_blossom', 2, [0.85, 1.05], 1.4, 5.6), s('tree_small', 2, [0.9, 1.1], 1.2, 4.4, { tint: leafVar })],
  edgeUnder: [s('bush', 3, [0.9, 1.2], 0.9, 1.3, { tint: leafVar }), s('flowers', 3, [1, 1.3], 0.8, 0.4, { detail: true }), s('hay_bale', 1, [0.9, 1.1], 0.8, 1.2), s('barrel', 1, [0.9, 1.1], 0.5, 1.1), s('crates', 1, [0.9, 1.1], 0.9, 1.6)],
  farTree: { leaf: 0x5f9a42, kind: 'round' },
  obstacleTrees: [s('tree_round', 3, [0.9, 1.2], 1.6, 6.2, { tint: leafVar }), s('tree_blossom', 2, [0.9, 1.1], 1.4, 5.6)],
  obstacleRocks: [s('rock_big', 1, [0.8, 1.1], 1.4, 1.4, { tint: stoneVar })],
  decor: [s('flowers', 3, [0.9, 1.2], 0.8, 0.4, { detail: true }), s('flowers_red', 1, [0.9, 1.2], 0.8, 0.4, { detail: true })],
  decorDensity: 0.35,
  grass: { perYd2: 0.35, colors: [0x5a8c38, 0x82ab4c], height: 0.45 },
  sky: { top: 0x3f86e0, mid: 0x9cc6f2, horizon: 0xf6e6cc, cloud: 0xfffaf2, cloudShade: 0xc0c0e4 },
  lighting: {
    background: 0xc4d8ea,
    fog: [0.66, 0.74, 0.84],
    hemiSky: 0xdcecff,
    hemiGround: 0x7a7a52,
    hemiIntensity: 0.74,
    key: 0xfff0dc,
    lift: [0.014, 0.012, 0.024],
    fogNear: 70,
    fogFar: 190,
    fogAmt: 0.0,
    keyIntensity: 3.0,
  },
};

export const OUTDOOR_THEMES: Record<string, OutdoorTheme> = { meadow: MEADOW, forest: FOREST, town: TOWN };

/** Outdoor theme for a field map (unknown theme ids fall back to the meadow). */
export function outdoorThemeFor(d: DungeonDef | undefined): OutdoorTheme | null {
  if (!d?.field) return null;
  return OUTDOOR_THEMES[d.field.theme] ?? MEADOW;
}

/**
 * A DungeonTheme shell for the renderer plumbing that expects one (lighting, ambience room
 * styles, footstep surfaces). The field builder never uses its kits / surfaces.
 */
export function outdoorDungeonTheme(t: OutdoorTheme): DungeonTheme {
  const room: RoomStyle = { archetype: `outdoor_${t.id}`, floor: t.id === 'town' ? 'grass' : 'grass', light: 'neutral', ceiling: 'none' };
  return {
    id: `field_${t.id}`,
    name: t.name,
    floors: { grass: { tint: t.ground.grass[0], tileYd: 4 } },
    surfaces: {
      ledgeTop: { tint: 0xffffff, tileYd: 4 },
      stone: { tint: 0xffffff, tileYd: 4 },
      cliff: { tint: 0xffffff, tileYd: 4 },
      trim: { tint: 0xffffff, tileYd: 4 },
      wood: { tint: 0xffffff, tileYd: 4 },
    },
    arch: { ledgeHeight: 0, roomMargin: 0, corridorMargin: 0, wallThickness: 0, wallHeights: { far: 0, near: 0, side: 0 }, baseY: -2, trim: false },
    room: (_r: RoomDef) => room,
    corridorFloor: () => 'grass',
    kits: {},
    heroes: {},
    lighting: t.lighting,
    glow: { warm: [1.0, 0.6, 0.25], jade: [0.3, 0.85, 0.8] },
  };
}
